import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MonoTypeOperatorFunction, Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';

export type QueryParamValue = string | number | boolean | readonly (string | number)[];
export type QueryParams = Record<string, QueryParamValue | null | undefined>;

function toHttpParams(params?: HttpParams | QueryParams): HttpParams | undefined {
  if (!params) return undefined;
  if (params instanceof HttpParams) return params;

  let http = new HttpParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined) continue;
    if (Array.isArray(value)) {
      for (const item of value) http = http.append(key, String(item));
    } else {
      http = http.set(key, String(value));
    }
  }
  return http;
}

function logResponse<T>(method: string, url: string): MonoTypeOperatorFunction<T> {
  return tap({
    error: (err) => {
      console.error(`[api] ✗ ${method} ${url}`, err);
    },
  });
}

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiUrl;

  get<T>(path: string, params?: HttpParams | QueryParams): Observable<T> {
    const httpParams = toHttpParams(params);
    const fullUrl = this.buildDebugUrl(path, httpParams);
    return this.http
      .get<T>(`${this.baseUrl}${path}`, { params: httpParams })
      .pipe(logResponse<T>('GET', fullUrl));
  }

  post<T>(path: string, body: unknown): Observable<T> {
    return this.http
      .post<T>(`${this.baseUrl}${path}`, body)
      .pipe(logResponse<T>('POST', `${this.baseUrl}${path}`));
  }

  put<T>(path: string, body: unknown): Observable<T> {
    return this.http
      .put<T>(`${this.baseUrl}${path}`, body)
      .pipe(logResponse<T>('PUT', `${this.baseUrl}${path}`));
  }

  patch<T>(path: string, body: unknown): Observable<T> {
    return this.http
      .patch<T>(`${this.baseUrl}${path}`, body)
      .pipe(logResponse<T>('PATCH', `${this.baseUrl}${path}`));
  }

  delete<T>(path: string, body?: unknown): Observable<T> {
    return this.http
      .delete<T>(`${this.baseUrl}${path}`, body === undefined ? {} : { body })
      .pipe(logResponse<T>('DELETE', `${this.baseUrl}${path}`));
  }

  private buildDebugUrl(path: string, params?: HttpParams): string {
    const qs = params?.toString();
    return qs ? `${this.baseUrl}${path}?${qs}` : `${this.baseUrl}${path}`;
  }
}
